import * as React from 'react';

import {
    Button,
    TextField,
    Typography
} from '@material-ui/core';
import {
    createStyles,
    WithStyles,
    withStyles
} from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { IconButton, InputAdornment } from '@material-ui/core';
import { Close, Visibility, VisibilityOff } from '@material-ui/icons';
import { SimpleModal } from '../../shared/components/simple-modal';

const styles = (theme: Theme) => createStyles({
    root: {
        padding: '1em',
        width: '30em'
    },
    header: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between'
    },
    body: {
        display: 'flex',
        flexDirection: 'column'
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.grey[100],
        borderRadius: 4,
        padding: '0.5em 0.75em',
        marginBottom: '0.5em'
    },
    userEmail: {
        fontWeight: 600,
        color: theme.palette.text.primary
    },
    userId: {
        color: theme.palette.text.secondary,
        fontFamily: 'monospace'
    },
    field: {
        width: '100%'
    },
    footer: {
        margin: '0.5em',
        width: '100%'
    },
    button: {
        alignSelf: 'flex-start',
        marginRight: '1em'
    }
});

interface Props extends WithStyles<typeof styles> {
    open: boolean;
    userId: string;
    email: string;
    onSubmit?: (userId: string, email: string, password: string, passwordConfirmation: string) => void;
    onClose?: () => void;
}

interface State {
    password: string;
    passwordConfirmation: string;
    showPassword: boolean;
    showPasswordConfirmation: boolean;
}

const emptyState: State = {
    password: '',
    passwordConfirmation: '',
    showPassword: false,
    showPasswordConfirmation: false
};

class PasswordResetModal extends React.Component<Props, State> {
    state: State = { ...emptyState };

    onFieldChanged = (field: 'password' | 'passwordConfirmation') =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            this.setState({ [field]: event.target.value } as Pick<State, typeof field>);
        }

    onSubmit = () => {
        const { userId, email, onSubmit } = this.props;
        const { password, passwordConfirmation } = this.state;
        if (onSubmit) {
            onSubmit(userId, email, password, passwordConfirmation);
        }
        this.reset();
    }

    onClose = () => {
        const { onClose } = this.props;
        this.reset();
        if (onClose) {
            onClose();
        }
    }

    reset = () => {
        this.setState({ ...emptyState });
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.open && !this.props.open) {
            this.reset();
        }
    }

    toggleShowPassword = () => {
        this.setState(prev => ({ showPassword: !prev.showPassword }));
    }

    toggleShowPasswordConfirmation = () => {
        this.setState(prev => ({ showPasswordConfirmation: !prev.showPasswordConfirmation }));
    }

    public render() {
        const { classes, open, email, userId } = this.props;
        const { password, passwordConfirmation, showPassword, showPasswordConfirmation } = this.state;

        const passwordsMatch = password === passwordConfirmation;
        const isSubmitDisabled = !password || !passwordConfirmation || !passwordsMatch;
        const showMismatchError = !!passwordConfirmation && !passwordsMatch;

        return (
            <SimpleModal
                open={open}
                className={classes.root}
                header={
                    <div className={classes.header}>
                        <Typography variant="h5">
                            Reset Password
                        </Typography>
                        <IconButton onClick={this.onClose}>
                            <Close />
                        </IconButton>
                    </div>
                }
                footer={
                    <div className={classes.footer}>
                        <Button
                            className={classes.button}
                            variant="contained"
                            color="secondary"
                            disabled={isSubmitDisabled}
                            onClick={this.onSubmit}
                            data-testid="password-reset-submit-button"
                        >
                            Submit
                        </Button>
                        <Button
                            className={classes.button}
                            variant="contained"
                            color="default"
                            onClick={this.onClose}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            >
                <div className={classes.body}>
                    <div className={classes.userInfo}>
                        <Typography variant="body2" className={classes.userEmail}>
                            {email}
                        </Typography>
                        <Typography variant="caption" className={classes.userId}>
                            {userId}
                        </Typography>
                    </div>
                    <TextField
                        id="password-reset-password"
                        label="New Password"
                        type={showPassword ? 'text' : 'password'}
                        className={classes.field}
                        value={password}
                        onChange={this.onFieldChanged('password')}
                        margin="normal"
                        placeholder="New Password"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={this.toggleShowPassword}
                                        size="small"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        id="password-reset-confirmation"
                        label="Confirm Password"
                        type={showPasswordConfirmation ? 'text' : 'password'}
                        className={classes.field}
                        value={passwordConfirmation}
                        onChange={this.onFieldChanged('passwordConfirmation')}
                        margin="normal"
                        placeholder="Confirm Password"
                        InputLabelProps={{ shrink: true }}
                        error={showMismatchError}
                        helperText={showMismatchError ? 'Passwords do not match' : ''}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={this.toggleShowPasswordConfirmation}
                                        size="small"
                                        aria-label={showPasswordConfirmation ? 'Hide password confirmation' : 'Show password confirmation'}
                                    >
                                        {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>
            </SimpleModal>
        );
    }
}

const MUIComponent = withStyles(styles)(PasswordResetModal);
export { MUIComponent as PasswordResetModal };
